import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type JobStore } from '@/features/jobs/JobStore'
import { task } from 'mobx-task'

/**
 * Confirms notifying every available pro of the selected workspace's
 * unclaimed jobs.
 */
export class NotifyProsOfUnclaimedJobsDialogViewModel {
  /**
   * View model for the underlying modal.
   */
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getSelectedWorkspaces: () => Id[],
  ) {}

  /**
   * Notifies all pros of unclaimed jobs in the selected workspace.
   */
  notifyPros = task.resolved(async () => {
    const workspaces = this._getSelectedWorkspaces()
    if (workspaces.length !== 1) {
      return
    }

    const workspaceId = workspaces[0]
    const jobsNotified = await this._jobStore
      .bulkNotifyProsOfOpenJobs(workspaceId)
      .catch(this._createAlertErrorHandler('Notifying pros failed'))
      .catch(() => null)
    if (jobsNotified === null) {
      return
    }

    this.modal.close()

    if (jobsNotified === 0) {
      this._newAlert()
        .withState('neutral')
        .saying('No unclaimed jobs found in this workspace.')
        .autoDismiss()
        .show()
    } else {
      this._newAlert()
        .withState('success')
        .saying(
          `All available pros were successfully notified of ${jobsNotified} unclaimed job${jobsNotified !== 1 ? 's' : ''}.`,
        )
        .autoDismiss()
        .show()
    }
  })
}
